//React
import { FunctionComponent, useState, useRef, useEffect } from "react";
//Google Maps
import {
	AdvancedMarker,
	InfoWindow,
	useAdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
//Components
import { Result } from "../../../results/models/result";
import ResultCard from "../../../results/components/result-list/result-card";
import ResultCardQmi from "../../../results/components/result-list/result-card-qmi";
//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";
//Selectors
import { selectIsMobile } from "../../../system/redux/selectors/select-is-mobile";
import { selectIsQmiFinder } from "../../../system/redux/selectors/select-is-qmi-finder";
import { setSelectedResult } from "../../../results/redux/slices/result-list-slice";
import { setIsMobileCardOpen } from "../../../system/redux/slices/app-status-slice";

//Assets
const pin = require("../../assets/icon_location-pin.png");
const pinHover = require("../../assets/icon_location-pin-action.png");
const hotwPin = require("../../assets/hotw-pin.png");
const hotwPinHover = require("../../assets/hotw-pin-active.png");

interface AdvanceMarkerInfoWindowProps {
	result: Result;
	markerOpacity: number;
	zIndex: number;
	selected: boolean;
	mapCenter: { lat: number; lng: number };
	zoomLevel: number;
	onMarkerHover: (uniqueId: string) => void;
	onMarkerLeave: () => void;
	isInfoWindowOpen: boolean;
}

export const AdvancedMarkerInfoWindow: FunctionComponent<
	AdvanceMarkerInfoWindowProps
> = (props) => {
	//Properties
	const {
		result,
		markerOpacity,
		zIndex,
		selected,
		mapCenter,
		zoomLevel,
		onMarkerHover,
		onMarkerLeave,
		isInfoWindowOpen,
	} = props;

	//React Hooks
	const [markerRef, marker] = useAdvancedMarkerRef();
	const timeoutRef = useRef(null);

	// Local state to manage info window visibility
	const [localInfoWindowOpen, setLocalInfoWindowOpen] = useState(false);

	// Sync local state with parent's state
	useEffect(() => {
		setLocalInfoWindowOpen(isInfoWindowOpen);
	}, [isInfoWindowOpen]);

	//State Values
	const isMobile = useAppSelector(selectIsMobile);
	const isQmiFinder = useAppSelector(selectIsQmiFinder);

	//Store Dispatch
	const dispatch = useAppDispatch();

	//Functions
	//Show the hover pin when a user hovers over a pin.
	function getIcon() {
		if (localInfoWindowOpen || selected) {
			if (isQmiFinder) {
				return hotwPinHover;
			} else {
				return pinHover;
			}
		}

		if (isQmiFinder) {
			return hotwPin;
		} else {
			return pin;
		}
	}

	function getOffsetPosition(): [number, number] {
		const markerLat = result.position.latitude;
		const markerLng = result.position.longitude;
		const centerLat = mapCenter.lat;
		const centerLng = mapCenter.lng;

		const latDirection = markerLat > centerLat ? -1 : 1;
		const lngDirection = markerLng > centerLng ? -1 : 1;
		if (latDirection === -1 && lngDirection === 1) {
			return [194, 275]; // top-left
		} else if (latDirection === -1 && lngDirection === -1) {
			return [-194, 275]; // top-right
		} else if (latDirection === 1 && lngDirection === -1) {
			return [-194, 40]; // bottom-right
		} else {
			return [194, 40]; // bottom-left
		}
	}

	// --- NEW HANDLER FUNCTIONS WITH DELAY ---
	// This handler is for the AdvancedMarker itself
	const handleMarkerMouseOver = () => {
		// Only update state if not on mobile
		if (!isMobile) {
			setLocalInfoWindowOpen(true);
			onMarkerHover(result.uniqueId);
		}
	};

	const handleMarkerMouseOut = () => {
		if (!isMobile) {
			setLocalInfoWindowOpen(false);
			onMarkerLeave();
		}
	};

	//Set the opacity of the pin
	const pinStyle = {
		opacity: markerOpacity,
		boxShadow: "none",
		outline: "none",
	};

	function handleMarkerClick(e) {
		if (isMobile) {
			e.stopPropagation();
			dispatch(setIsMobileCardOpen(true));
			dispatch(setSelectedResult(result));
		} else {
			window.location.href = result.url;
		}
	}
	//JSX
	return (
		<div
			style={{ boxShadow: "none", outline: "none" }}
			onClick={handleMarkerClick}
			className="advanced-marker"
		>
			<AdvancedMarker
				ref={markerRef}
				position={{
					lat: result.position.latitude,
					lng: result.position.longitude,
				}}
				title={result.title}
				style={pinStyle}
				zIndex={zIndex}
			>
				<div
					onMouseEnter={handleMarkerMouseOver}
					onMouseLeave={handleMarkerMouseOut}
					style={{ boxShadow: "none", outline: "none" }}
				>
					<img alt="Map Marker Icon" src={getIcon()} />
				</div>
			</AdvancedMarker>
			{localInfoWindowOpen && (
				<InfoWindow
					position={{
						lat: result.position.latitude,
						lng: result.position.longitude,
					}}
					pixelOffset={getOffsetPosition()}
					maxWidth={200}
					disableAutoPan={true}
					onCloseClick={() => {
						setLocalInfoWindowOpen(false);
						onMarkerLeave();
					}}
				>
					
						{ isQmiFinder &&
							<div className="map-home-info qmi-info-window">
								<ResultCardQmi result={result} isInInfoWindow={true} />
							</div>
						}
						{!isQmiFinder &&
							<div className="map-home-info">
								<ResultCard result={result} isInInfoWindow={true} />
							</div>
						}
						
				</InfoWindow>
			)}
		</div>
	);
};

export default AdvancedMarkerInfoWindow;
